import axios from 'axios';
import { notification } from 'antd';
import history from '../history';
import { AUTH_TOKEN, REFRESH_TOKEN, API_URL, PUBLIC_PATH_PREFIX } from '../configs/AppConfig';
import { logout } from '../utils/logout';

const service = axios.create({
  baseURL: API_URL,
  timeout: 60000,
});

// Config
const ENTRY_ROUTE = '/auth/login';
const TOKEN_PAYLOAD_KEY = 'token';
const PUBLIC_REQUEST_KEY = 'public-request';

// Emailed links (MRF approval, interview feedback, pre-joining form) land on
// /public/* pages. A recipient who is also an app user may have a stale login
// token in localStorage, which makes RootContext fire /api/app calls that 401 —
// those must never log out or redirect someone off a public page.
const isOnPublicPage = () => window.location.pathname.startsWith(PUBLIC_PATH_PREFIX);

// Exchanges the refresh token for a new access token. Shared by every request that
// 401s at the same time so the (single-use, rotating) refresh token is spent once.
let refreshPromise = null;
const refreshAccessToken = () => {
  if (!refreshPromise) {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);
    refreshPromise = axios
      .post(`${API_URL}/api/auth/refresh`, { refreshToken })
      .then((res) => {
        const { token, refreshToken: newRefreshToken } = res.data.data || res.data;
        localStorage.setItem(AUTH_TOKEN, token);
        localStorage.setItem(REFRESH_TOKEN, newRefreshToken);
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// API Request interceptor
service.interceptors.request.use(
  (config) => {
    const jwtToken = localStorage.getItem(AUTH_TOKEN) || null;

    if (jwtToken) {
      config.headers[TOKEN_PAYLOAD_KEY] = jwtToken;
    }

    if (
      !jwtToken &&
      !config.headers[PUBLIC_REQUEST_KEY] &&
      !isLoginRelatedApi(config) &&
      !isOnPublicPage()
    ) {
      history.push(ENTRY_ROUTE);
      window.location.reload();
    }

    return config;
  },
  (error) => {
    // Do something with request error here
    notification.error({
      message: 'Error',
    });
    Promise.reject(error);
  }
);

// API respone interceptor
service.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config;

    // Surface the backend's actual error message (res.BadRequest/DbError puts it in
    // `message`, though some call sites pass it as the first `data` arg instead) so
    // that `err.message` in calling code shows real text instead of axios's generic
    // "Request failed with status code {n}".
    const backendMessage = error?.response?.data?.message || error?.response?.data?.data;
    if (backendMessage && typeof backendMessage === 'string') {
      error.message = backendMessage;
    }

    if (isOnPublicPage()) {
      return Promise.reject(error);
    }

    // Access token expired: silently get a new one and replay the request once.
    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retried &&
      !isLoginRelatedApi(originalRequest) &&
      localStorage.getItem(REFRESH_TOKEN)
    ) {
      originalRequest._retried = true;
      try {
        await refreshAccessToken();
        return service(originalRequest);
      } catch (refreshError) {
        // Refresh token expired/revoked � fall through to the session-expired logout.
      }
    }

    if (status === 401) {
      notification.error({
        message: "Session Expired",
        description: "Please login again",
      });

      logout();
      return Promise.reject(error);
    }

    if (status === 404) {
      notification.error({ message: "Not Found" });
    }

    if (status === 500) {
      notification.error({ message: "Internal Server Error" });
      logout();
    }

    if (status === 508) {
      notification.error({ message: "Time Out" });
    }

    return Promise.reject(error);
  }
);

const isLoginRelatedApi = (config) => {
  const { url } = config;
  return url.includes('/auth');
};
export default service;
